import { clone, difference, findWhere } from "underscore";
import { useCallback, useEffect, useState } from "react";
import { useRef, useMemo } from "react";
import { Row, Col } from "reactstrap";

import { Field, LayoutAndSections, Module } from "../../types/TenantInterfaces";
import { RecordSummarySection, RichTextSection } from "./RecordDetailLayout";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";
import { Prefill, PrefillPair } from "../../types/GlobalInterfaces";
import { generateTimePrefills } from "../../utils/QuickCreateUtils";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import SubitemRenderer from "./Subitems/SubitemRenderer";
import AppSpinner from "../../elements/AppSpinner";
import SectionRenderer from "./SectionRenderer";
import FieldUtils from "../../utils/FieldUtils";
import Constants from "../../utils/Constants";

type RecordOrNull = Record<string, any> | null;

interface Props {
  prefill: PrefillPair[];
  variableValues: any;
  module: Module | null | undefined;
  disableReadonlyFields?: boolean;
  layout?: LayoutAndSections | null;
  onChange: (values: any) => void;
}

/**
 * Main renderer for creating records, subitems
 * Things to consider -> Deafult Values, Prefills, Workspace Prefills, Read Only
 *
 */
const CreateRecordRendererV2 = ({
  prefill,
  variableValues,
  module,
  disableReadonlyFields = false,
  layout,
  onChange,
}: Props) => {
  const { workspace, workspaceLoading } = useWorkspace();
  const [payload, setPayload] = useState<Record<string, any>>({});
  const [startingPayload, setStartingPayload] = useState<RecordOrNull>(null);
  const [defaultSubitem, setDefaultSubitem] = useState<RecordOrNull>(null);
  const workspaceRef = useRef<any>();
  const payloadRef = useRef<any>();
  payloadRef.current = payload;
  workspaceRef.current = workspace;

  const wsPrefills = JSON.stringify(workspace?.Prefills);

  // Check if this data source has any workspace prefills. They will override all other prefills later on
  const workspacePrefill = useMemo(() => {
    const prefillObject = findWhere(workspaceRef.current?.Prefills, {
      ModuleAPIName: module?.APIName,
    });

    return prefillObject?.Prefills || null;
  }, [module?.APIName, wsPrefills]);

  const handleChange = useCallback(
    (field: Field, val: Record<string, any>, updatingSubitem = false) => {
      if (!field) return;

      setPayload((prev: any) => {
        // Shallow clone is VERY important here. We don't want the Subitems to rerender.
        // Deep cloning would drastically decrease performance!
        const payload2: any = clone(prev) || {};

        if (updatingSubitem && typeof payload2[field.APIName] === "object") {
          if (field.FieldType === "MultiSubitem") {
            payload2[field.APIName] = val;
          } else {
            payload2[field.APIName] = {
              ...payload2[field.APIName],
              ...val,
            };
          }
        } else {
          payload2[field.APIName] = val;
        }

        callFunctionIfExists(onChange, payload2);
        return payload2;
      });
    },
    []
  ); // No dependencies is important here

  // Initialize defaultSubitem, and payload after loading is finished.
  useEffect(() => {
    if (workspaceLoading || !Array.isArray(module?.Fields)) return;
    const defaultSub: Record<string, any> = {};
    const newPayload = { ...(payloadRef.current ?? {}) };

    for (const field of module?.Fields ?? []) {
      if (!field) continue;

      const foundPrefill = findWhere<any>(prefill, {
        Field: field.APIName,
      });

      if (foundPrefill?.Value) {
        newPayload[field.APIName] = foundPrefill?.Value;
      } else if (
        field?.DefaultValue &&
        typeof field.DefaultValue === "string"
      ) {
        newPayload[field.APIName] = field.DefaultValue.startsWith("{{")
          ? generateDynamicDefVals(field.FieldType, field?.DefaultValue)
          : field?.DefaultValue;
      } else if (
        field?.DefaultValue !== undefined &&
        field?.DefaultValue !== null
      ) {
        // Calculated Default value could be a currency object
        newPayload[field.APIName] =
          field?.FieldType === "Calculated" && field?.DefaultValue?.Value
            ? field?.DefaultValue?.Value
            : field?.DefaultValue;
      } else {
        newPayload[field.APIName] = "";
      }

      const foundWorkspacePrefill = findWhere(workspacePrefill, {
        Field: field.APIName,
      });

      if (foundWorkspacePrefill) {
        newPayload[field.APIName] = foundWorkspacePrefill.Value;
      }
    }

    setStartingPayload(newPayload);
    setPayload(newPayload);
    onChange(newPayload);
    setDefaultSubitem(defaultSub);
  }, [workspaceLoading, module?.Fields, prefill]);

  const loading =
    !module?.Fields || !startingPayload || !defaultSubitem || !layout?.Sections;

  return (
    <>
      {loading ? (
        <div style={{ display: "grid", placeItems: "center", height: "100%" }}>
          <AppSpinner size="xl" />
        </div>
      ) : (
        <Row>
          <Col xs="12">
            <RecordInputSections
              module={module}
              startingPayload={startingPayload}
              defaultSubitem={defaultSubitem}
              layout={layout}
              handleChange={handleChange}
              prefill={prefill}
              workspacePrefill={workspacePrefill}
              variableValues={variableValues}
              disableReadonlyFields={disableReadonlyFields}
              payload={payload}
            />
            {layout?.Default &&
              module?.Fields.map((f) => {
                if (!FieldUtils.fieldTypeIsSubitem(f?.FieldType)) return null;

                return (
                  <SubitemRenderer
                    key={f.APIName}
                    module={module}
                    field={f}
                    prefill={prefill}
                    subItemValues={payload[f.APIName]}
                    defaultSub={defaultSubitem[f.APIName]}
                    handleChange={handleChange}
                    disableReadonlyFields={disableReadonlyFields}
                  />
                );
              })}
          </Col>
        </Row>
      )}
    </>
  );

  function generateDynamicDefVals(fieldType: string, value: any) {
    if (
      [Constants.FIELDTYPES.DATE, Constants.FIELDTYPES.DATE_TIME].includes(
        fieldType
      )
    ) {
      const timeDefault = generateTimePrefills({ value });

      if (!timeDefault) return "";

      return timeDefault.Default || timeDefault.Value;
    }
    return value;
  }
};

export default CreateRecordRendererV2;

/**
 * Generates the record input sections
 * Uses any template from the module, before the default additional information section.
 */
function RecordInputSections({
  module,
  startingPayload,
  defaultSubitem,
  layout,
  handleChange,
  prefill,
  workspacePrefill,
  variableValues,
  disableReadonlyFields,
  payload,
}: {
  module: Module;
  startingPayload: Record<string, any> | null;
  defaultSubitem: any;
  layout: LayoutAndSections;
  handleChange: any;
  prefill?: PrefillPair[];
  workspacePrefill?: Prefill;
  variableValues?: Record<string, any>;
  disableReadonlyFields?: boolean;
  payload?: any;
}) {
  // TODO: Console log sections, add Typescript type.
  const inputSections = useMemo(() => {
    const sections: (RecordSummarySection | RichTextSection)[] = [];

    const moduleSections = layout?.Sections;
    const moduleFields = module?.Fields;

    if (!moduleSections || !moduleFields) return sections;

    const matchedFields: (Field | string)[] = []; // The fields that were included in the RecordDetailView template. (So they can be ignored in Additional section)
    const allFields = [];

    for (let i = 0; i < moduleFields.length; i++) {
      const field = moduleFields[i];

      // Skip subitems - We handle them separately (due to them needing payload instead of finalPayload)
      if (FieldUtils.fieldTypeIsSubitem(field.FieldType)) continue;

      allFields.push(field.APIName);
    }

    if (Array.isArray(moduleSections) && moduleSections.length) {
      for (let i = 0; i < moduleSections.length; i++) {
        const section = moduleSections[i];

        if (section.Type === "RecordSummarySection") {
          sections.push(section);

          for (let j = 0; j < section.Fields?.length; j++) {
            matchedFields.push(section.Fields[j].Field);
          }
        } else if (section.Type === "RichTextSection") {
          sections.push(section);

          section.Field && matchedFields.push(section.Field);
        }
      }
    }

    if (layout?.Default) {
      const additionalSection: RecordSummarySection = {
        id: "AdditionalInformation",
        Title: "Additional Information",
        Fields: [],
        Type: "RecordSummarySection",
      };

      // All remaining fields are added to the Additional Information section
      const missingFields = difference(allFields, matchedFields);

      for (let i = 0; i < missingFields.length; i++) {
        additionalSection.Fields.push({
          Field: missingFields[i],
          ColSpan: 1,
        });
      }

      additionalSection.Fields.length && sections.push(additionalSection);
    }

    return sections;
  }, [layout?.Sections, module?.Fields]);

  if (
    !module?.Fields ||
    !startingPayload ||
    !defaultSubitem ||
    !layout?.Sections
  )
    return null;

  return (
    <>
      {inputSections.map((s: any, i: number) => {
        return (
          <SectionRenderer
            key={i}
            section={s}
            detailViewLayoutID={layout?.id ?? ""}
            module={module}
            createMode={true}
            values={startingPayload}
            handleChange={handleChange}
            prefill={prefill}
            readonlyPrefill={workspacePrefill}
            variableValues={variableValues}
            payload={payload}
          />
        );
      })}
    </>
  );
}
